import * as React from "react"
import {useIntl, FormattedMessage} from "react-intl"
import {AnimatePresence} from "framer-motion"
import styled from "styled-components"
import {
  border,
  color,
  layout,
  position,
  shadow,
  space,
  typography,
} from "styled-system"

import format from "date-fns/format"
import differenceInCalendarDays from "date-fns/differenceInCalendarDays"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faBriefcaseMedical} from "@fortawesome/free-solid-svg-icons/faBriefcaseMedical"
import {faCheckCircle} from "@fortawesome/pro-solid-svg-icons/faCheckCircle"
import {faCreditCard} from "@fortawesome/pro-regular-svg-icons/faCreditCard"
import {faDoNotEnter} from "@fortawesome/pro-solid-svg-icons/faDoNotEnter"
import {faEnvelopeOpenText} from "@fortawesome/pro-regular-svg-icons/faEnvelopeOpenText"
import {faInfoCircle} from "@fortawesome/pro-solid-svg-icons/faInfoCircle"
import {faFileSignature} from "@fortawesome/pro-regular-svg-icons/faFileSignature"
import {faCaretDown} from "@fortawesome/pro-solid-svg-icons/faCaretDown"
import {faCaretUp} from "@fortawesome/pro-solid-svg-icons/faCaretUp"
import {faTimes} from "@fortawesome/pro-regular-svg-icons/faTimes"

import DatePicker from "react-datepicker"
import {Listbox, ListboxOption} from "@reach/listbox"
import Tooltip from "@reach/tooltip"

import NotificationsContext from "../../contexts/notifications.js"

import Storage from "../../services/storage.js"
import useDebounce from "../../hooks/debounce.js"
import useFetcher from "../../hooks/fetcher.js"

import Flex from "../../components/flex.js"
import Loader from "../../components/loader.js"
import {MotionRow} from "../../components/motion.js"
import Row from "../../components/row.js"
import Text from "../../components/text.js"
import View from "../../components/view.js"

import Stay from "./components/stay.js"
import {StyledInput, StyledTr} from "../../components/styled.js"

// import fixturesConfig from "./fixtures-config.js"
// import fixturesData from "./fixtures-data.js"

const columnToIcon = {
  bank_auth_state: faCreditCard,
  dgc_scans: faBriefcaseMedical,
  police_form: faFileSignature,
  prestay_form: faEnvelopeOpenText,
}
const minWidth = width => `min-width: ${width}px;`
const width = width =>
  `width: ${width}px; min-width: ${width}px; max-width: ${width}px;`
const getWidthFromColumn = column => {
  switch (column) {
    case "done":
      return width(20)
    case "avatar":
      return width(60)
    case "name":
      return minWidth(150)
    case "stay":
      return width(120)
    case "bank_auth_value":
      return width(95)
    case "bank_auth_state":
    case "dgc_scans":
    case "police_form":
    case "prestay_form":
    case "actions":
      return width(35)
    case "bank_debit_shortcut":
      return width(160)
    case "arrival_time":
      return width(80)
    default:
      return ""
  }
}

const Notifications = styled(View).attrs({
  position: "fixed",
  top: "10px",
  left: "calc(50% - 175px)",
  right: "calc(50% - 175px)",
  width: "notification",
})`
  ${position}
  z-index: 20;
`
const Notification = styled(MotionRow).attrs({
  backgroundColor: "white",
  border: "notification",
  boxShadow: "notification",
  fontSize: "normal",
  p: "10px",
  mb: 2,
})`
  ${color} ${border} ${shadow} ${space} ${typography}
`
const NotificationStatus = styled(Flex).attrs({
  nogrow: true,
  noshrink: true,
  flexBasis: "11px",
  mr: "20px",
})``
const NotificationClose = styled(Flex).attrs({
  nogrow: true,
  noshrink: true,
  flexBasis: "11px",
  ml: "20px",
})``

const StyledDateWrapper = styled(Row).attrs({
  display: "inline-flex",
  nogrow: true,
  noshrink: true,
  fontSize: "listbox",
  alignItems: "center",
})`
  cursor: pointer;
  &::after {
    position: absolute;
    right: 10px;
    font-size: 0.5em;
    content: "▼";
  }
`
const StyledDateInput = styled(StyledInput).attrs({
  px: 2,
})`
  ${layout}
`
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`
const StyledThead = styled.thead``

const StyledTheadTr = styled(StyledTr).attrs({
  height: "60px",
  borderBottomWidth: "2px",
})`
  ${border} ${layout}
`
const StyledTh = styled.th.attrs({
  backgroundColor: "background",
  position: "sticky",
  zIndex: 20,
  top: 0,
  textAlign: "left",
  verticalAlign: "middle",
})`
  ${props => getWidthFromColumn(props.column)}
  ${color} ${layout} ${position} ${typography}

  ${Text} {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`

const OrderIcon = styled(FontAwesomeIcon).attrs({ml: 1})`
  cursor: pointer;
  ${space}
`

const OneStyledDateInput = React.forwardRef((props, ref) => {
  const intl = useIntl()

  let value = intl.formatDate(props.value)
  const diff = differenceInCalendarDays(Date.parse(props.value), new Date())
  if (diff <= 2 && diff >= -2) {
    value = intl.formatRelativeTime(diff, "day", {
      numeric: "auto",
      style: "long",
    })
  }
  return <StyledDateInput ref={ref} {...props} value={value} />
})

const clientID =
  document.location?.search
    .split("&")
    .find(str => str.includes("clientID"))
    .split("=")[1] ?? 0
if (!clientID) throw new Error("no clientID given")

const storage = Storage.for("arrival", null, {expiration: 5 * 60 * 1000})
const storedDate = storage.get("date", new Date().toString())

const Arrivals = () => {
  const intl = useIntl()
  const {notifications, removeNotification} =
    React.useContext(NotificationsContext)

  const [direction, setDirection] = React.useState("arrival")
  const [date, setDate] = React.useState(new Date(storedDate))
  const [rawSearch, setRawSearch] = React.useState("")
  const [search] = useDebounce(rawSearch, 50)
  const [sort, setSort] = React.useState(null)
  const [order, setOrder] = React.useState("asc")

  const [stays, setStays] = React.useState({})

  const {loading: configLoading, data: config} = useFetcher(
    `home/clients/${clientID}/arrivals/listconfig?flow=${direction}`
  )
  const {loading: dataLoading, data} = useFetcher(
    `home/clients/${clientID}/arrivals/list?flow=${direction}&date=${format(
      date,
      "yyyy-MM-dd"
    )}`,
    {debug: true, nocache: true, polling: 30000, pollingParameter: "lastupdate"}
  )
  // const configLoading = false
  // const dataLoading = false
  // const config = JSON.parse(fixturesConfig[direction])
  // const data = JSON.parse(fixturesData[direction])

  const setSorts = (column, order) => {
    setSort(column)
    setOrder(order)
  }

  React.useEffect(() => {
    setSorts("name", "asc")
  }, [])

  React.useEffect(() => {
    if (!notifications.length) return
    const timer = setTimeout(() => {
      const [first] = notifications
      removeNotification(first)
    }, 4000)
    return () => clearTimeout(timer)
  }, [notifications.length, removeNotification])

  React.useEffect(() => {
    if (!data) return
    setStays(prev => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== true)
      ),
    }))
  }, [data])

  if (configLoading) {
    return (
      <Row justifyContent="center" pt="120px">
        <Loader size="6x" />
      </Row>
    )
  }

  if (configLoading || !config || !data) return null

  const cachedData = Array.isArray(data)
    ? []
    : Object.entries(data).map(([id, stay]) => {
        return [id, stay === true ? stays[id] : stay]
      })

  const filteredData = search?.length
    ? cachedData.filter(([_, line]) => {
        return Object.entries(line).some(([row, cell]) => {
          if (
            [
              "avatar",
              "bank_auth_state",
              "police_form",
              "prestay_form",
              "actions",
              "done",
            ].includes(row)
          )
            return false
          if (row === "stay") {
            return (
              cell.value.arrival.toLowerCase().includes(search.toLowerCase()) ||
              cell.value.departure
                .toLowerCase()
                .includes(search.toLowerCase()) ||
              cell.value.idresa.toLowerCase().includes(search.toLowerCase())
            )
          }
          if (typeof cell?.value === "string") {
            return cell.value.toLowerCase().includes(search.toLowerCase())
          }
          if (typeof cell?.value === "number") {
            return String(cell.value)
              .toLowerCase()
              .includes(search.toLowerCase())
          }
          if (typeof cell === "string") {
            return cell.toLowerCase().includes(search.toLowerCase())
          }
          return false
        })
      })
    : cachedData

  const sortedData = filteredData
    ? filteredData.sort(([_k1, v1], [_k2, v2]) => {
        if (order === "asc") {
          return typeof v1[sort]?.sortValue === "number"
            ? v1[sort]?.sortValue - v2[sort]?.sortValue
            : v1[sort]?.sortValue?.localeCompare(v2[sort]?.sortValue)
        }
        return typeof v1[sort]?.sortValue === "number"
          ? v2[sort]?.sortValue - v1[sort]?.sortValue
          : v2[sort]?.sortValue?.localeCompare(v1[sort]?.sortValue)
      })
    : filteredData

  const handleChangeDate = newDate => {
    storage.set("date", newDate)
    setDate(newDate)
  }

  if (process.env.APP_ENV === "development") {
    config.currency = {
      code: "EUR",
      symbol: "€",
    }
  }

  if (process.env.__DEBUG__ === "true") {
    // eslint-disable-next-line no-console
    // console.log({config, data, filteredData, sortedData})
  }

  return (
    <View mt={10}>
      {notifications.length ? (
        <Notifications>
          {notifications.map((notification, index) => (
            <AnimatePresence key={index}>
              <Notification
                initial={{height: 0}}
                animate={{height: "auto"}}
                exit={{height: 0}}
                transition={{duration: 0.25}}
              >
                <NotificationStatus>
                  <FontAwesomeIcon
                    icon={
                      notification.type === "success"
                        ? faCheckCircle
                        : notification.type === "info"
                        ? faInfoCircle
                        : faDoNotEnter
                    }
                    color={
                      notification.type === "success"
                        ? "#2abb9c"
                        : notification.type === "info"
                        ? "#ffa500"
                        : "#ea0909"
                    }
                  />
                </NotificationStatus>
                <Flex justifyContent="center">
                  <Text>{notification.message}</Text>
                </Flex>
                <NotificationClose nogrow noshrink>
                  <FontAwesomeIcon
                    icon={faTimes}
                    size="lg"
                    onClick={() => removeNotification(notification)}
                    style={{cursor: "pointer"}}
                  />
                </NotificationClose>
              </Notification>
            </AnimatePresence>
          ))}
        </Notifications>
      ) : null}
      <Row zIndex={1}>
        <Listbox
          value={direction}
          onChange={setDirection}
          style={{fontWeight: "bold"}}
        >
          <ListboxOption value="arrival">
            <FormattedMessage id="arrivals.direction.arrival" />
          </ListboxOption>
          <ListboxOption value="departure">
            <FormattedMessage id="arrivals.direction.departure" />
          </ListboxOption>
        </Listbox>
        <Flex nogrow width="11px" />
        <StyledDateWrapper>
          <DatePicker
            selected={date}
            onChange={handleChangeDate}
            customInput={<OneStyledDateInput />}
          />
        </StyledDateWrapper>
        <Row>
          <Text nowrap ml={2}>
            <Text>
              <FormattedMessage
                id="arrivals.total"
                values={{
                  direction,
                  value: sortedData?.length ?? 0,
                  b: chunks => <b>{chunks}</b>,
                }}
              />
            </Text>
          </Text>
        </Row>
        <FormattedMessage id="arrivals.search" />
        <Flex nogrow width="150px">
          <StyledInput
            ml={2}
            onChange={event => setRawSearch(event.target.value)}
          />
        </Flex>
      </Row>
      <Row>
        <StyledTable>
          <StyledThead>
            <StyledTheadTr>
              {Object.keys(config.columns).map(key => {
                const column = config.columns[key]
                return (
                  <StyledTh key={key} column={key}>
                    <Row alignItems="center">
                      <Flex nogrow>
                        <Text bold uppercase>
                          {column.icon ? (
                            <Tooltip
                              label={
                                <Text>
                                  <FormattedMessage
                                    id={`arrivals.column.${key}`}
                                  />
                                </Text>
                              }
                            >
                              <span>
                                <FontAwesomeIcon icon={columnToIcon[key]} />
                              </span>
                            </Tooltip>
                          ) : column.title ? (
                            typeof column.title === "string" ? (
                              column.title
                            ) : typeof column.title === "object" ? (
                              column.title?.[intl.locale] ?? column.title.en
                            ) : (
                              <FormattedMessage
                                id={`arrivals.column.${key}`}
                                defaultMessage={key}
                              />
                            )
                          ) : null}
                        </Text>
                      </Flex>
                      {column.sortable ? (
                        <Flex>
                          {order === "desc" && sort === key ? (
                            <OrderIcon
                              onClick={() => setSorts(key, "asc")}
                              icon={faCaretUp}
                            />
                          ) : (
                            <OrderIcon
                              onClick={() => setSorts(key, "desc")}
                              icon={faCaretDown}
                            />
                          )}
                        </Flex>
                      ) : null}
                    </Row>
                  </StyledTh>
                )
              })}
            </StyledTheadTr>
          </StyledThead>
          <tbody>
            {dataLoading ? (
              <tr>
                <td
                  colSpan={Object.keys(config.columns).length}
                  style={{textAlign: "center", paddingTop: "20px"}}
                >
                  <Loader size="6x" />
                </td>
              </tr>
            ) : (
              sortedData.map(([id, row]) => (
                <Stay
                  key={id}
                  id={id}
                  clientID={clientID}
                  row={row}
                  config={config}
                />
              ))
            )}
          </tbody>
        </StyledTable>
      </Row>
    </View>
  )
}

export default Arrivals
